import { Opt } from 'app/tools/general.types';
import { Observable, pluck } from 'rxjs';
import { ApiUrl } from '../api-core/api-constants';
import { StatusResp } from '../api-core/api-core.types';
import { apiGet, apiPost } from '../api-core/api-request';
import apiItemHookFactory from '../api-core/utility-factories/api-item-hook-factory';
import apiRequestHookFactory from '../api-core/utility-factories/api-request-hook-factory';
import apiResourceHookFactory from '../api-core/utility-factories/api-resource-hook-factory';
import { apiRedux } from '../api-redux';
import {
  CreateProductDefectReq,
  ProductDefect,
} from '../product-defect/api-product-defect.types';
import {
  EventEntity,
  InitiateChatReq,
  QuotePayment,
  QuoteSummery,
  QuoteSummeryUpdate,
  RepairCase,
  RepairerSearchReq,
  RepairerSearchRtn,
  RepairStatusUpdate,
  SendChatEvent,
  SendChatImageEvent,
  WarrantyClaimEvent,
  WarrantyClaimPayment,
  TransactionQuote,
  BankStatus,
} from './api-repairer.types';

export const useSearchRepairers = apiRequestHookFactory(
  apiRedux.repairers,
  (data: RepairerSearchReq): Observable<RepairerSearchRtn> =>
    apiGet<RepairerSearchRtn>(ApiUrl.REPAIRERS, {
      urlParams: {
        assignedProductId: data.assignedProductId,
        lat: data.lat.toString(),
        long: data.long.toString(),
        maxDistance: data.maxDistance.toString(),
      },
    })
);

export const useCreateDefect = apiRequestHookFactory(
  apiRedux.createProductDefect,
  (data: CreateProductDefectReq): Observable<ProductDefect> =>
    apiPost<ProductDefect>(ApiUrl.PRODUCT_DEFECTS, data)
);

export const useInitiateChat = apiRequestHookFactory(
  apiRedux.initiateRepairChat,
  (data: InitiateChatReq): Observable<RepairCase> =>
    apiPost<RepairCase>(ApiUrl.REPAIR_CASES, data)
);

export const useGetChatList = apiResourceHookFactory(
  apiRedux.getRepairCases,
  () => apiGet<RepairCase[]>(ApiUrl.REPAIR_CASES)
);

export const useRepairerCase = apiItemHookFactory(
  apiRedux.repairCase,
  (id: string) => apiGet<RepairCase>(`${ApiUrl.REPAIR_CASES}/${id}`)
);

export const useQuoteSummery = apiItemHookFactory(
  apiRedux.quoteSummery,
  (id: string) =>
    apiGet<QuoteSummery>(`${ApiUrl.QUOTE_SUMMERY}/${id}`)
);

export const useCreateRepairEvent = apiRequestHookFactory(
  apiRedux.createRepairEvent,
  (event: SendChatEvent) =>
    apiPost<EventEntity>(ApiUrl.REPAIR_EVENTS, event)
);

export const useWarrantyClaim = apiRequestHookFactory(
  apiRedux.setWarrantyClaim,
  (event: WarrantyClaimEvent) =>
    apiPost<StatusResp>(ApiUrl.WARRANTY_CLAIM, event)
);

export const useWarrantyClaimPayment = apiRequestHookFactory(
  apiRedux.setWarrantyClaimPayment,
  (event: WarrantyClaimPayment) =>
    apiPost<StatusResp>(ApiUrl.WARRANTY_CLAIM_PAYMENT, event)
);

export const useCreateRepairImageEvent = apiRequestHookFactory(
  apiRedux.createRepairImageEvent,
  (event: SendChatImageEvent) =>
    apiPost<EventEntity>(ApiUrl.REPAIR_IMAGE_EVENT, event)
);

export const useAssignmentOpenRepair = apiItemHookFactory(
  apiRedux.assignmentOpenRepair,
  (assignmentId: number) =>
    apiGet<{ repairCaseId: Opt<number> }>(
      ApiUrl.ASSIGNMENT_OPEN_REPAIR,
      { urlParams: { assignedProduct: assignmentId.toFixed() } }
    ).pipe(pluck('repairCaseId'))
);

export const useUpdateQuoteSummery = apiRequestHookFactory(
  apiRedux.setQuoteSummery,
  (summery: QuoteSummeryUpdate) =>
    apiPost<StatusResp>(
      `${ApiUrl.QUOTE_SUMMERY_UPDATE}/${summery.id}`,
      summery
    )
);

export const useQuotePayment = apiRequestHookFactory(
  apiRedux.setQuotePayment,
  (quote: QuotePayment) =>
    apiPost<StatusResp>(`${ApiUrl.QUOTE_SUMMERY_PAYMENT}`, quote)
);

export const useUpdateStatus = apiRequestHookFactory(
  apiRedux.setUpdateStatus,
  (data: RepairStatusUpdate) =>
    apiPost<StatusResp>(`${ApiUrl.REPAIR_UPDATE_STATUS}`, data)
);

export const useGetRepairerHistory = apiResourceHookFactory(
  apiRedux.getRepairerHistory,
  (): Observable<TransactionQuote[]> =>
    apiGet<TransactionQuote[]>(ApiUrl.REPAIRER_HISTORY)
);

export const useTransactionDetail = apiResourceHookFactory(
  apiRedux.getRepairerTransaction,
  (id: number) =>
    apiGet<TransactionQuote[]>(`${ApiUrl.REPAIRER_HISTORY}/${id}`)
);

export const useRepaircasePaymentDetail = apiResourceHookFactory(
  apiRedux.getRepaircasePaymentDetail,
  (id: number) =>
    apiGet<TransactionQuote[]>(
      `${ApiUrl.REPAIRCASE_PAYMENT_DETAIL}/${id}`
    )
);

export const useGetBankStatus = apiResourceHookFactory(
  apiRedux.getRepairerBankStatus,
  () => apiGet<BankStatus>(ApiUrl.REPAIRER_ACCOUNT_STATUS)
);
